<script setup>
import { computed, ref, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';

import { useSidebarContext } from './provider';
import { useEventListener } from '@vueuse/core';

const props = defineProps({
  isExpanded: { type: Boolean, default: false },
  label: { type: String, required: true },
  icon: { type: [Object, String], required: true },
  children: { type: Array, default: undefined },
  activeChild: { type: Object, default: undefined },
});

const { isAllowed } = useSidebarContext();
const scrollableContainer = ref(null);

// v2.0.23: Local expand/collapse state — collapsed by default
const isSubGroupExpanded = ref(false);

const toggleSubGroup = () => {
  isSubGroupExpanded.value = !isSubGroupExpanded.value;
};

const accessibleItems = computed(() =>
  props.children.filter(child => {
    return child.to && isAllowed(child.to);
  })
);

const hasAccessibleItems = computed(() => {
  return accessibleItems.value.length > 0;
});

// v2.0.23: Auto-expand if active child is inside this subgroup
const hasActiveChildInside = computed(() => {
  if (!props.activeChild) return false;
  return props.children.some(child => child.name === props.activeChild.name);
});

// Auto-expand when active child is detected inside
watch(
  hasActiveChildInside,
  hasActive => {
    if (hasActive) {
      isSubGroupExpanded.value = true;
    }
  },
  { immediate: true }
);

const isScrollable = computed(() => {
  return accessibleItems.value.length > 7;
});

const scrollEnd = ref(false);

useEventListener(scrollableContainer, 'scroll', () => {
  const { scrollHeight, scrollTop, clientHeight } = scrollableContainer.value;
  scrollEnd.value = scrollHeight - scrollTop === clientHeight;
});

// v2.0.23: Whether to show children (both parent group AND local subgroup must be expanded)
const showChildren = computed(() => {
  return props.isExpanded && isSubGroupExpanded.value;
});
</script>

<template>
  <!-- v2.0.23: Clickable subgroup header with toggle chevron -->
  <div
    v-if="hasAccessibleItems"
    v-show="isExpanded"
    class="flex items-center gap-2 px-2 py-1.5 rounded-lg h-8 text-n-slate-10 select-none cursor-pointer hover:bg-n-alpha-2 transition-colors my-1"
    @click="toggleSubGroup"
  >
    <Icon v-if="icon" :icon="icon" class="size-4" />
    <span class="text-sm font-medium leading-5 flex-grow">
      {{ label }}
    </span>
    <span
      class="size-3.5 transition-transform duration-200"
      :class="
        isSubGroupExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
      "
    />
  </div>
  <ul v-if="children.length" class="m-0 list-none reset-base relative group">
    <div
      ref="scrollableContainer"
      :class="{
        'max-h-[calc(14rem+16px)] overflow-y-scroll no-scrollbar': isScrollable,
      }"
    >
      <SidebarGroupLeaf
        v-for="child in children"
        v-show="showChildren || activeChild?.name === child.name"
        v-bind="child"
        :key="child.name"
        :active="activeChild?.name === child.name"
      />
    </div>
  </ul>
</template>
